import { provideHttpClient } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    inject,
    Injectable,
    LOCALE_ID,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { provideToastr } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { AuthUtils } from './core/auth/auth.utils';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { PaginatorIntlService } from './shared/paginatorIntlService';
import { Utils } from './shared/utils';

import { registerLocaleData } from '@angular/common';
import localeHr from '@angular/common/locales/hr';
import localeSl from '@angular/common/locales/sl';

registerLocaleData(localeSl, 'sl');
// registerLocaleData(localeHr, 'hr');

@Injectable()
export class CustomDateAdapter extends LuxonDateAdapter {
    override getFirstDayOfWeek(): number {
        return 1; // Monday as the first day of the week
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        provideToastr(),

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    // {
                    //     id: 'hr',
                    //     label: 'Croatian',
                    // },
                    {
                        id: 'sl',
                        label: 'Slovenian',
                    },
                ],
                defaultLang: 'sl',
                fallbackLang: 'sl',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                if (localStorage.getItem('language')) {
                    translocoService.setActiveLang(
                        localStorage.getItem('language')
                    );
                } else {
                    translocoService.setActiveLang(defaultLang);
                }

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },
        {
            provide: MatPaginatorIntl,
            useFactory: (translate) => {
                const service = new PaginatorIntlService();
                service.injectTranslateService(translate);
                return service;
            },
            deps: [TranslocoService],
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'dense',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),

        Utils,
        AuthUtils,

        { provide: LOCALE_ID, useValue: 'sl' }, // Set default locale to Croatian

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter,
        },
        // {
        //     provide: MAT_DATE_FORMATS,
        //     useValue: {
        //         parse: {
        //             dateInput: 'D',
        //         },
        //         display: {
        //             dateInput: 'DDD',
        //             monthYearLabel: 'LLL yyyy',
        //             dateA11yLabel: 'DD',
        //             monthYearA11yLabel: 'LLLL yyyy',
        //         },
        //     },
        // },
        // {
        //     provide: MAT_DATE_LOCALE, useValue: 'hr', // Use 'hr' for Croatian
        // },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'd.M.yyyy',
                },
                display: {
                    dateInput: 'd.M.yyyy',
                    monthYearLabel: 'MMMM yyyy',
                    dateA11yLabel: 'd. MMMM yyyy',
                    monthYearA11yLabel: 'MMMM yyyy',
                },
            },
        },
    ],
};
